import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import authClient from '#/auth/authClient'
import { useOrganization } from '#/lib/queries'
import { useEntityNotifications } from '#/lib/queries/entityNotifications'
import { Button } from '#/components/ui/button'
import { ArrowLeft } from 'lucide-react'
import { PrefetchData } from '#/components/PrefetchData'
import Header from '#/components/header'

export const Route = createFileRoute('/_authenticated/$slug')({
  component: OrganizationLayout,
})

function OrganizationLayout() {
  const { slug } = Route.useParams()
  const navigate = useNavigate()
  const { data: session, isPending: isSessionPending } = authClient.useSession()
  const { data: activeOrg } = authClient.useActiveOrganization()
  const [isSyncingActiveOrg, setIsSyncingActiveOrg] = useState(false)

  const { data: organization, isLoading: orgLoading, error: orgError } = useOrganization(slug)

  useEntityNotifications(organization?.id)

  const member = organization?.members?.find(
    (m: { userId: string }) => m.userId === session?.user?.id
  )

  useEffect(() => {
    if (isSessionPending) return
    if (session === null) {
      const accountUrl = import.meta.env.VITE_QUICKBACK_ACCOUNT_URL || '/account'
      window.location.href = `${accountUrl}/login?redirect=${encodeURIComponent(window.location.href)}`
    }
  }, [session, isSessionPending])

  useEffect(() => {
    if (!organization?.slug || !session?.user) return

    const activeOrgId = (session.session as any)?.activeOrganizationId || activeOrg?.id
    if (activeOrgId === organization.id) {
      setIsSyncingActiveOrg(false)
      return
    }

    let cancelled = false
    setIsSyncingActiveOrg(true)

    const syncActiveOrganization = async () => {
      try {
        await authClient.organization.setActive({ organizationSlug: organization.slug })
        await authClient.getSession({ query: { disableCookieCache: true } })
      } catch (err) {
        console.warn('Failed to set active organization from slug', err)
      } finally {
        if (!cancelled) setIsSyncingActiveOrg(false)
      }
    }

    void syncActiveOrganization()
    return () => { cancelled = true }
  }, [organization?.id, organization?.slug, session?.user, (session?.session as any)?.activeOrganizationId, activeOrg?.id])

  if (isSessionPending || !session || orgLoading || isSyncingActiveOrg) {
    return (
      <>
        <PrefetchData slug={slug} />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-pulse text-muted-foreground">
            {isSyncingActiveOrg ? 'Switching organization...' : 'Loading...'}
          </div>
        </div>
      </>
    )
  }

  if (orgError || (!organization && !orgLoading)) {
    return (
      <>
        <PrefetchData slug={slug} />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center space-y-4 max-w-md">
            <div className="text-destructive text-lg font-semibold">Error</div>
            <p className="text-muted-foreground">{orgError?.message || 'Organization not found'}</p>
            <Button onClick={() => window.location.href = (import.meta.env.VITE_QUICKBACK_ACCOUNT_URL || '/account') + '/profile'} variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Profile
            </Button>
          </div>
        </div>
      </>
    )
  }

  if (!organization || !member) {
    return (
      <>
        <PrefetchData slug={slug} />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center space-y-4 max-w-md">
            <div className="text-destructive text-lg font-semibold">
              {!organization ? 'Loading...' : 'Access Denied'}
            </div>
            {member === undefined && organization && (
              <p className="text-muted-foreground">You don&apos;t have access to this organization</p>
            )}
            <Button onClick={() => window.location.href = (import.meta.env.VITE_QUICKBACK_ACCOUNT_URL || '/account') + '/profile'} variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Profile
            </Button>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <PrefetchData slug={slug} />
      <Header />
      <div className="flex-1 flex flex-col p-4 min-h-0">
        <Outlet />
      </div>
    </>
  )
}
